import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Search, Loader2, CheckCircle, AlertCircle, Lock, Globe, ChevronDown } from 'lucide-react';
import type { SiteTemplate } from '../../schemas/validation';
import { useAuth } from '../../hooks/useAuth';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';
import { useAdminTemplateManagerState } from './useAdminTemplateManagerState';
import { useAdminTemplateManagerHandlers } from './useAdminTemplateManagerHandlers';
import { TemplateTable } from './TemplateTable';
import { EditTemplateModal } from './EditTemplateModal';
import { DeleteTemplateModal } from './DeleteTemplateModal';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';

const PAGE_SIZE = 10;

function templateMatchesSearch(template: SiteTemplate, searchTerm: string): boolean {
  const term = searchTerm.toLowerCase();
  return (
    template.name.toLowerCase().includes(term) ||
    (template.description?.toLowerCase().includes(term) ?? false) ||
    (template.tags?.toLowerCase().includes(term) ?? false)
  );
}

export const AdminTemplateManager: React.FC = () => {
  const { logout } = useAuth();
  const state = useAdminTemplateManagerState();
  const handlers = useAdminTemplateManagerHandlers({ state });
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    handlers.loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      state.setSearchTerm(value);
      state.setCurrentPage(1);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        handlers.handleSearch(value);
      }, 300);
    },
    [state, handlers]
  );

  // Filter templates client-side for instant feedback while debounced search runs
  const displayedTemplates = state.searchTerm
    ? state.templates.filter(template => templateMatchesSearch(template, state.searchTerm))
    : state.templates;
  const displayedPrivate = (state.privateTemplates ?? []).filter(template =>
    state.searchTerm ? templateMatchesSearch(template, state.searchTerm) : true
  );
  const activeTemplates = state.showPrivate ? displayedPrivate : displayedTemplates;
  const totalPages = Math.max(1, Math.ceil(activeTemplates.length / PAGE_SIZE));
  const currentPage = Math.min(state.currentPage, totalPages);
  const pageTemplates = activeTemplates.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const isTableLoading = state.showPrivate ? state.isLoadingPrivate : state.isLoading;

  useEffect(() => {
    if (state.currentPage > totalPages) {
      state.setCurrentPage(totalPages);
    }
  }, [state, totalPages]);

  return (
    <div className="page-container">
      <AdminDashboardHeader onLogout={logout} />

      {/* Toast Messages */}
      {state.successMessage && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg shadow-lg">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">{state.successMessage}</span>
          </div>
        </div>
      )}
      {state.error && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">{state.error}</span>
            <button
              onClick={() => state.setError(null)}
              className="ml-2 text-red-600 hover:text-red-800"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="page-content max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg">
            <button
              type="button"
              onClick={() => setHelpOpen(open => !open)}
              aria-expanded={helpOpen}
              className="flex w-full items-center justify-between p-4 text-left"
            >
              <span className="text-sm font-semibold text-blue-900">
                Private and public templates
              </span>
              <ChevronDown
                size={16}
                className={`text-blue-900 transition-transform ${helpOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {helpOpen && (
              <div className="space-y-2 px-4 pb-4 text-sm text-blue-800">
                <p>
                  <strong>Private</strong> templates are visible only to their owner. They stay off
                  the library, the import picker, and Roary.
                </p>
                <p>
                  <strong>Public</strong> templates can be chosen by anyone when starting a site or
                  importing one. Roary builds new websites from public templates. Write a specific
                  description so Roary knows when to choose each one.
                </p>
                <p>
                  Build a site, then use Save as Site Template in the site manager. You can switch
                  Private or Public later from Edit Template.
                </p>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="card p-4">
              <div className="text-sm text-gray-500">Public Templates</div>
              <div className="text-2xl font-bold text-gray-900">
                {state.templates.filter(t => t.isPublic).length}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-sm text-gray-500">With Description</div>
              <div className="text-2xl font-bold text-gray-900">
                {state.templates.filter(t => t.description).length}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-sm text-gray-500">Total Uses</div>
              <div className="text-2xl font-bold text-gray-900">
                {state.templates.reduce((sum, t) => sum + t.usageCount, 0)}
              </div>
            </div>
          </div>

          {/* Template List Card */}
          <div className="card">
            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={state.searchTerm}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="Search templates by name, description, or tags..."
                    className="input-field pl-10"
                  />
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <div className="inline-flex rounded-md" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={!state.showPrivate}
                      onClick={() => void handlers.selectTab('public')}
                      className={`inline-flex items-center gap-2 rounded-l-md border px-3 py-1.5 text-sm font-medium ${
                        state.showPrivate
                          ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'border-indigo-600 bg-indigo-600 text-white'
                      }`}
                    >
                      <Globe size={14} />
                      Public
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={state.showPrivate}
                      onClick={() => void handlers.selectTab('private')}
                      className={`inline-flex items-center gap-2 rounded-r-md border border-l-0 px-3 py-1.5 text-sm font-medium ${
                        state.showPrivate
                          ? 'border-indigo-600 bg-indigo-600 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Lock size={14} />
                      Private
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">
                    {isTableLoading ? 'Loading...' : `${activeTemplates.length} template(s)`}
                  </span>
                </div>
              </div>
            </div>

            {isTableLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
                  <p className="text-gray-600">
                    {state.showPrivate ? 'Loading private templates...' : 'Loading templates...'}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <TemplateTable
                  templates={pageTemplates}
                  searchTerm={state.searchTerm}
                  onEdit={handlers.startEdit}
                  onDelete={handlers.startDelete}
                  onPreview={handlers.startPreview}
                />
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                      {Math.min(currentPage * PAGE_SIZE, activeTemplates.length)} of{' '}
                      {activeTemplates.length} templates
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => state.setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-500">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => state.setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {state.showEditModal && state.selectedTemplate && (
        <EditTemplateModal
          template={state.selectedTemplate}
          isSubmitting={state.isSubmitting}
          onSave={handlers.handleUpdateTemplate}
          onCancel={() => {
            state.setShowEditModal(false);
            state.setSelectedTemplate(null);
          }}
        />
      )}

      {state.showDeleteModal && state.selectedTemplate && (
        <DeleteTemplateModal
          template={state.selectedTemplate}
          isSubmitting={state.isSubmitting}
          onConfirm={handlers.handleDeleteTemplate}
          onCancel={() => {
            state.setShowDeleteModal(false);
            state.setSelectedTemplate(null);
          }}
        />
      )}

      {state.showPreviewPanel && state.selectedTemplate && (
        <TemplatePreviewPanel
          template={state.selectedTemplate}
          onClose={() => {
            state.setShowPreviewPanel(false);
            state.setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminTemplateManager;
